<template>
  <div class="carga-ots">
    <!-- Rango compartido por las dos sub-pestañas: arranca igual que el reporte de plan de
         producción (horarios de la empresa) y se puede ajustar a mano. -->
    <div class="filtros">
      <VueCtkDateTimePicker v-model="filtros.inicio" :disabled="cargando" :max-date="filtros.fin || undefined"
        format="YYYY-MM-DD HH:mm" formatted="YYYY-MM-DD HH:mm" first-day-of-week="1" noHeader overlay
        label="Inicio" button-now-translation="Ahora" class="f-input" />
      <VueCtkDateTimePicker v-model="filtros.fin" :disabled="cargando" :min-date="filtros.inicio || undefined"
        format="YYYY-MM-DD HH:mm" formatted="YYYY-MM-DD HH:mm" first-day-of-week="1" noHeader overlay
        label="Fin" button-now-translation="Ahora" class="f-input" />
      <v-btn small color="primary" :loading="cargando" @click="buscar">
        <v-icon left small>mdi-magnify</v-icon> Buscar
      </v-btn>
      <v-btn small text :disabled="cargando" @click="restablecerRango"
        title="Volver al rango que sale de los horarios de la empresa">
        <v-icon left small>mdi-calendar-refresh-outline</v-icon> Semana en curso
      </v-btn>
    </div>

    <v-tabs v-model="tab" background-color="transparent" class="mt-2">
      <v-tab>Información</v-tab>
      <v-tab>Comparativa</v-tab>

      <v-tabs-items v-model="tab" touchless>
        <v-tab-item eager>
          <Informacion ref="informacion" @cargando="onCargando" />
        </v-tab-item>
        <v-tab-item eager>
          <Comparativa ref="comparativa" @cargando="onCargando" />
        </v-tab-item>
      </v-tabs-items>
    </v-tabs>
  </div>
</template>

<script>
import axios from 'axios'
import Informacion from './CargaOts/Informacion.vue'
import Comparativa from './CargaOts/Comparativa.vue'

// Lee una propiedad aceptando camelCase o PascalCase (el back serializa en PascalCase).
const prop = (obj, camel) => {
  if (!obj) return undefined
  if (obj[camel] !== undefined) return obj[camel]
  const pascal = camel.charAt(0).toUpperCase() + camel.slice(1)
  return obj[pascal]
}

export default {
  name: 'CargaOts',

  components: { Informacion, Comparativa },

  data() {
    return {
      tab: 0,
      cargando: false,
      listo: false,
      // Cada sub-pestaña se consulta la primera vez que se abre y se marca como cargada;
      // "Buscar" o un cambio de rango las invalida para que vuelvan a pedir datos.
      cargadas: [false, false],
      filtros: {
        inicio: '',
        fin: '',
      },
      // Fin del rango inicial: 'semana' (una semana exacta) u 'horarioEmpresa' (última hora
      // del último día configurado). Se lee de la config del reporte de plan de producción
      // (r100) para que esta pestaña arranque con el mismo rango que ese reporte.
      modoFinPeriodo: 'semana',
    }
  },

  watch: {
    tab() {
      this.cargarActiva()
    },
  },

  async mounted() {
    // Mismo arranque que el reporte de plan de producción: primero la config que decide cómo
    // se calcula el fin del rango, y luego el rango a partir de los horarios activos de la
    // empresa (CAT_Horarios). Solo si eso falla se cae a la semana en curso.
    await this.cargarModoFinPeriodo()
    await this.inicializarDesdeHorarios()
    this.listo = true
    this.cargarActiva()
  },

  methods: {
    // Punto de entrada desde la pestaña de Producción (Lista.vue).
    initialize() {
      if (!this.listo) return
      this.buscar()
    },

    onCargando(valor) {
      this.cargando = !!valor
    },

    buscar() {
      this.cargadas = [false, false]
      this.cargarActiva()
    },

    // Consulta la sub-pestaña visible si todavía no tiene datos del rango vigente.
    cargarActiva() {
      if (!this.listo || this.cargadas[this.tab]) return
      const hijo = this.tab === 0 ? this.$refs.informacion : this.$refs.comparativa
      if (!hijo) return
      this.$set(this.cargadas, this.tab, true)
      hijo.cargar(this.filtroAIso(this.filtros.inicio), this.filtroAIso(this.filtros.fin))
    },

    async restablecerRango() {
      await this.inicializarDesdeHorarios()
      this.buscar()
    },

    // ===================== Rango por defecto (horarios de la empresa) =====================

    // Formatea una fecha al formato del picker: "YYYY-MM-DD HH:mm".
    fmtFiltro(d) {
      const p = (n) => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
    },

    // Convierte el valor del picker ("YYYY-MM-DD HH:mm" o "YYYY-MM-DD") a ISO para el query.
    filtroAIso(v) {
      if (!v) return v
      const s = String(v).trim().replace(' ', 'T')
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return `${s}T00:00:00`
      if (/T\d{2}:\d{2}$/.test(s)) return `${s}:00`
      return s
    },

    // Lunes (00:00) de la semana en curso.
    lunesSemanaActual() {
      const hoy = new Date()
      const diff = (hoy.getDay() + 6) % 7 // lunes = 0
      const lunes = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate())
      lunes.setDate(lunes.getDate() - diff)
      return lunes
    },

    // Fallback síncrono: semana en curso (lunes 00:00 → lunes siguiente 00:00).
    inicializarSemana() {
      const inicio = this.lunesSemanaActual()
      const fin = new Date(inicio)
      fin.setDate(inicio.getDate() + 7)
      this.filtros.inicio = this.fmtFiltro(inicio)
      this.filtros.fin = this.fmtFiltro(fin)
    },

    // Calcula el rango por defecto a partir de los horarios activos (CAT_Horarios): inicia en
    // la primera hora del día con menor diaSemana (0=domingo … 6=sábado). Según la configuración
    // del reporte, termina una semana después o en la última hora del día con mayor diaSemana.
    async inicializarDesdeHorarios() {
      try {
        // /Horarios responde { events, start, intervals }: los horarios están en events.
        const { data } = await axios.get('/Horarios')
        const horarios = Array.isArray(data)
          ? data
          : Array.isArray(data && data.events)
            ? data.events
            : []
        if (!horarios.length) {
          this.inicializarSemana()
          return
        }

        const minutosHora = (valor) => {
          const [hora, minuto] = String(valor || '00:00').split(':').map(Number)
          return (hora || 0) * 60 + (minuto || 0)
        }
        const horariosValidos = horarios.filter((h) => {
          const dia = Number(prop(h, 'diaSemana'))
          return Number.isInteger(dia) && dia >= 0 && dia <= 6
        })
        if (!horariosValidos.length) {
          this.inicializarSemana()
          return
        }

        const primerDia = Math.min(...horariosValidos.map((h) => Number(prop(h, 'diaSemana'))))
        const ultimoDia = Math.max(...horariosValidos.map((h) => Number(prop(h, 'diaSemana'))))
        const primerHorario = horariosValidos
          .filter((h) => Number(prop(h, 'diaSemana')) === primerDia)
          .reduce((primero, h) =>
            minutosHora(prop(h, 'inicio')) < minutosHora(prop(primero, 'inicio')) ? h : primero,
          )
        const ultimoHorario = horariosValidos
          .filter((h) => Number(prop(h, 'diaSemana')) === ultimoDia)
          .reduce((ultimo, h) =>
            minutosHora(prop(h, 'fin')) > minutosHora(prop(ultimo, 'fin')) ? h : ultimo,
          )
        const [hhInicio, mmInicio] = String(prop(primerHorario, 'inicio') || '00:00').split(':').map(Number)
        const [hhFin, mmFin] = String(prop(ultimoHorario, 'fin') || '00:00').split(':').map(Number)

        // Última ocurrencia de ese día de la semana en o antes de hoy
        // (0=domingo … 6=sábado), p. ej. domingo → el último domingo desde hoy.
        const hoy = new Date()
        const inicio = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate())
        const diff = (inicio.getDay() - primerDia + 7) % 7
        inicio.setDate(inicio.getDate() - diff)
        inicio.setHours(hhInicio || 0, mmInicio || 0, 0, 0)

        const fin = new Date(inicio)
        if (this.modoFinPeriodo === 'horarioEmpresa') {
          fin.setDate(inicio.getDate() + (ultimoDia - primerDia))
          fin.setHours(hhFin || 0, mmFin || 0, 0, 0)
          // Un único horario puede cruzar medianoche (p. ej. 22:00 → 06:00).
          if (fin <= inicio) fin.setDate(fin.getDate() + 1)
        } else {
          fin.setDate(inicio.getDate() + 7)
        }

        this.filtros.inicio = this.fmtFiltro(inicio)
        this.filtros.fin = this.fmtFiltro(fin)
      } catch (e) {
        // Si falla, se usa el fallback de la semana en curso.
        this.inicializarSemana()
        // eslint-disable-next-line no-console
        console.error('Error cargando horarios para fechas por defecto', e)
      }
    },

    // Lee (sin escribir) la configuración del reporte de plan de producción para saber cómo
    // termina el rango. Si no existe o falla, se queda con el default 'semana'.
    async cargarModoFinPeriodo() {
      try {
        const { data } = await axios.get('ReportesConfig/Obtener/r100')
        let cfg = data
        const raw = prop(cfg, 'configJson')
        if (raw != null) cfg = typeof raw === 'string' ? JSON.parse(raw) : raw
        this.modoFinPeriodo = (cfg && cfg.modoFinPeriodo === 'horarioEmpresa')
          ? 'horarioEmpresa'
          : 'semana'
      } catch (e) {
        this.modoFinPeriodo = 'semana'
      }
    },
  },
}
</script>

<style scoped>
.carga-ots {
  padding: 12px;
}

.filtros {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.f-input {
  max-width: 240px;
  min-width: 180px;
}
</style>
